import * as RSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
  id?: string;
}

/**
 * Accessible select built on Radix — a styled, keyboard-navigable drop-in for `<select>`.
 * Same controlled contract as a native select (`value` + `onValueChange`).
 */
export function Select({
  label,
  hint,
  error,
  placeholder = 'Select…',
  value,
  onValueChange,
  options,
  disabled,
  required,
  name,
  className,
  id,
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? autoId;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={selectId} className="text-ink text-[13px] font-semibold">
          {label}
        </label>
      )}
      <RSelect.Root
        value={value}
        onValueChange={onValueChange}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(name !== undefined ? { name } : {})}
        {...(required !== undefined ? { required } : {})}
      >
        <RSelect.Trigger
          id={selectId}
          aria-invalid={error ? true : undefined}
          className={cn(
            'bg-surface group flex h-11 w-full items-center justify-between gap-2 rounded-xl border px-3.5',
            'text-ink text-sm outline-none transition-all duration-200',
            'data-[placeholder]:text-ink-subtle disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:border-gold-400 focus-visible:shadow-focus',
            error ? 'border-state-danger' : 'border-border-strong/35 hover:border-border-strong/60',
          )}
        >
          <RSelect.Value placeholder={placeholder} />
          <RSelect.Icon>
            <ChevronDown className="text-ink-subtle h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </RSelect.Icon>
        </RSelect.Trigger>

        <RSelect.Portal>
          <RSelect.Content
            position="popper"
            sideOffset={6}
            className={cn(
              'z-50 max-h-72 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl',
              'border-border bg-surface shadow-overlay border',
              'data-[state=open]:animate-scale-in',
            )}
          >
            <RSelect.Viewport className="ts-scroll p-1.5">
              {options.map((opt) => (
                <RSelect.Item
                  key={opt.value}
                  value={opt.value}
                  {...(opt.disabled !== undefined ? { disabled: opt.disabled } : {})}
                  className={cn(
                    'text-ink relative flex cursor-pointer select-none items-center rounded-lg py-2 pl-3 pr-8 text-sm',
                    'outline-none transition-colors duration-150',
                    'data-[highlighted]:bg-gold-50 data-[highlighted]:text-gold-800',
                    'data-[state=checked]:font-semibold',
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
                  )}
                >
                  <RSelect.ItemText>{opt.label}</RSelect.ItemText>
                  <RSelect.ItemIndicator className="absolute right-2.5 inline-flex items-center">
                    <Check className="text-gold-600 h-4 w-4" />
                  </RSelect.ItemIndicator>
                </RSelect.Item>
              ))}
            </RSelect.Viewport>
          </RSelect.Content>
        </RSelect.Portal>
      </RSelect.Root>
      {error ? (
        <p className="text-state-danger text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-ink-subtle text-xs">{hint}</p>
      ) : null}
    </div>
  );
}
